import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Printer, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canManageLabels } from '@/auth/AuthContext';
import { printLabelNodes } from './labelPrint';
import { DK_LABELS, DEFAULT_LABEL_SIZE, KIND_DEFAULT_SIZE, LABEL_KINDS, LABEL_SIZE_STORAGE, LABEL_STYLE, sizeById } from './labelSizes';
import { LabelTemplate } from './LabelTemplate';
import { code128Feasibility, planLabel } from './labelLayout';
import { LabelCanvas } from './LabelCanvas';
import { labelApi } from './labelApi';

/** Brother QL-800 label print dialog. labels = array of label-data objects (see labelSizes.js builders). */
export default function LabelPrintDialog({ open, onClose, labels = [], title }) {
  const { t } = useT();
  const { user } = useAuth();
  const kind = labels?.[0]?.kind || labels?.[0]?.type || null;
  // 種別に既定ロールがあるならそれを使う（記憶した誤ロールで開かないための安全弁）。
  const pinnedSize = KIND_DEFAULT_SIZE[kind] || null;
  const [size, setSize] = useState(() => {
    if (pinnedSize) return pinnedSize;
    try { return localStorage.getItem(LABEL_SIZE_STORAGE) || DEFAULT_LABEL_SIZE; } catch { return DEFAULT_LABEL_SIZE; }
  });
  // 初期の記号種はラベルデータ側で指定できる（未指定なら Code128）。
  // 技術資産・設備台帳も実機スキャン試験の結果 Code128 に戻したので、現在
  // format を指定しているラベル種は無い。
  const [format, setFormat] = useState(labels?.[0]?.format || 'code128');
  const [copies, setCopies] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState('');
  const previewRef = useRef(null);

  // ピン留め種別では共有の記憶値を上書きしない（他種別の選択を壊さないため）。
  useEffect(() => {
    if (pinnedSize) return;
    try { localStorage.setItem(LABEL_SIZE_STORAGE, size); } catch { /* ignore */ }
  }, [size, pinnedSize]);
  useEffect(() => {
    if (!open || !kind) return;
    labelApi.list({ kind }).then(setTemplates).catch(() => setTemplates([]));
  }, [open, kind]);

  const activeTpl = templates.find((tp) => tp.id === tplId) || null;

  const sz = DK_LABELS.find((s) => s.id === size) || DK_LABELS[0];
  const dieCut = DK_LABELS.filter((s) => s.group === 'die');
  const cont = DK_LABELS.filter((s) => s.group === 'cont');

  // 一括印刷: 識別子の無い記録は印刷対象から外し、黙って落とさず件名を出す。
  const all = labels || [];
  const skipped = all.filter((l) => l.identityMissing);
  const printable = all.filter((l) => !l.identityMissing);

  const expanded = [];
  printable.forEach((l) => { for (let i = 0; i < Math.max(1, Number(copies) || 1); i++) expanded.push(l); });
  // LabelPrintDialog は labels[0].kind から種別（項目カタログ）を決めるので、種別が
  // 混ざった一括選択は全行を先頭行のカタログで描いてしまう。混在は拒否する。
  const kindsInBatch = [...new Set(all.map((l) => l.kind || l.type).filter(Boolean))];
  const mixedKinds = kindsInBatch.length > 1;

  // 自動レイアウトが行を落とした／コードが下限を満たせない場合は黙って印刷しない。
  const plan = !activeTpl && printable[0] ? planLabel({ data: printable[0], w: sz.w, h: sz.h, format }) : null;
  const blocked = plan?.blocked || null;
  // このロールで Code128 が下限を満たせるか（ロケーション=従来レイアウトも含めて判定）。
  const c128 = !activeTpl && printable[0] ? code128Feasibility({ data: printable[0], w: sz.w, h: sz.h }) : null;
  const cantPrint = mixedKinds || printable.length === 0 || !!blocked
    || (format === 'code128' && !!c128?.tooNarrow) || !!plan?.tooNarrow;

  const doPrint = () => {
    if (!previewRef.current) return;
    const w = printLabelNodes(previewRef.current, sz, title);
    if (!w) { toast.error(t('mfg.label.popupBlocked')); return; }
    // 監査は「1枚=1件」。貼られた実物1枚を誰がいつ作ったか辿るのが目的なので、
    // 件数付きの操作単位1件では役に立たない。
    labelApi.logPrint({
      kind: kind || 'unknown', size_id: size, format,
      copies: Math.max(1, Number(copies) || 1),
      records: printable.map((l) => ({
        id: l.values?.sku || l.values?.equip_no || l.barcode || null,
        label: l.title || l.big || null,
      })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="label-print-dialog">
        <style>{LABEL_STYLE}</style>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag size={18} className="text-orange-500" weight="duotone" />
            {title || t('mfg.label.print')}{labels.length > 1 ? ` (${labels.length})` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-zinc-600">レイアウト</Label>
              {canManageLabels(user) && (
                <Link to="/settings/labels" data-testid="label-designer-link"
                  className="text-[11px] text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline"
                  onClick={onClose}>
                  {kind ? `${LABEL_KINDS[kind]?.label || kind} テンプレートを作成 →` : 'テンプレートを作成 →'}
                </Link>
              )}
            </div>
            {templates.length > 0 ? (
              <Select value={tplId || 'auto'} onValueChange={(v) => {
                const id = v === 'auto' ? '' : v; setTplId(id);
                const tp = templates.find((x) => x.id === id); if (tp) setSize(tp.size_id);
              }}>
                <SelectTrigger className="h-10 mt-1" data-testid="label-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">自動レイアウト（標準）</SelectItem>
                  {templates.map((tp) => (
                    <SelectItem key={tp.id} value={tp.id}>
                      {tp.name}（{sizeById(tp.size_id).w}{sizeById(tp.size_id).h ? `×${sizeById(tp.size_id).h}` : ''}mm）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1 rounded-lg border border-dashed border-zinc-200 bg-zinc-50/60 px-3 py-2 text-[11px] text-zinc-500" data-testid="label-no-templates">
                標準レイアウトで印刷します{kind && LABEL_KINDS[kind] ? `（「${LABEL_KINDS[kind].label}」用の` : '（'}カスタムテンプレートも作成できます）
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.label.size')}</Label>
              <Select value={size} onValueChange={setSize} disabled={!!activeTpl}>
                <SelectTrigger className="h-10 mt-1" data-testid="label-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t('mfg.label.dieCut')}</SelectLabel>
                    {dieCut.map((s) => <SelectItem key={s.id} value={s.id}>{t(s.nameKey)}</SelectItem>)}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>{t('mfg.label.continuous')}</SelectLabel>
                    {cont.map((s) => <SelectItem key={s.id} value={s.id}>{t(s.nameKey)}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.label.barcodeFormat')}</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="h-10 mt-1" data-testid="label-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="code128" disabled={!!c128?.tooNarrow}>
                    Code128{c128?.tooNarrow ? '（このロールでは細バーが下限未満）' : ''}
                  </SelectItem>
                  <SelectItem value="qr">QR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="w-32">
            <Label className="text-xs font-semibold text-zinc-600">{t('mfg.label.copies')}</Label>
            <Input type="number" min={1} max={50} value={copies} data-testid="label-copies-input"
              onChange={(e) => setCopies(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} className="h-10 mt-1" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600">{t('mfg.label.preview')} · {sz.roll} ({sz.w}{sz.h ? `×${sz.h}` : ''}mm)</Label>
            <p className="text-[11px] text-amber-600 mt-0.5" data-testid="label-roll-hint">
              ⚠️ QL-800 にセットされているロールと同じサイズを選んでください（不一致だと印刷できません）
            </p>
            {mixedKinds && (
              <p className="text-[11px] font-semibold text-rose-600 mt-1" data-testid="label-mixed-kinds">
                種別の異なるラベルが混在しています（{kindsInBatch.join('・')}）。同じ種別だけを選んでください。
              </p>
            )}
            {skipped.length > 0 && (
              <p className="text-[11px] font-semibold text-amber-700 mt-1" data-testid="label-skipped-records">
                {skipped.length}件は{skipped[0].identityMissing.field}未設定のため印刷しません:
                {' '}{skipped.map((l) => l.title || l.barcode).join('、')}
              </p>
            )}
            {blocked && (
              <p className="text-[11px] font-semibold text-rose-600 mt-1" data-testid="label-blocked">
                {blocked.message}
              </p>
            )}
            {plan?.tooNarrow && (
              <p className="text-[11px] font-semibold text-rose-600 mt-1" data-testid="label-code128-too-narrow">
                このロールでは Code128 の細バーが {plan.narrowMm.toFixed(3)}mm になり、下限 {plan.minNarrowMm}mm を満たせません
                （必要幅 {(plan.totalModules * plan.minNarrowMm).toFixed(1)}mm / 使用可 {plan.textWidthMm.toFixed(1)}mm）。
                QR を選ぶか、より大きいロールを使ってください。
              </p>
            )}
            {plan?.tooLong && (
              <p className="text-[11px] font-semibold text-rose-600 mt-1" data-testid="label-payload-too-long">
                このロールにはQRのデータが長すぎます（{plan.moduleMm == null ? 'モジュール数を計算できませんでした' : `${plan.moduleMm.toFixed(2)}mm/モジュール`} ·
                下限 {plan.minModuleMm}mm）。最大サイズで印刷しますが、読み取りに失敗する可能性があります。
                大きいロールを選んでください。
              </p>
            )}
            {plan?.dropped?.length > 0 && (
              <p className="text-[11px] text-zinc-500 mt-1" data-testid="label-dropped-lines">
                このロールでは入りきらないため次の行は印字されません（縦または横に不足 · コードの読み取りサイズを優先）:
                {' '}{plan.dropped.join('・')}
              </p>
            )}
            <div ref={previewRef} className="ql-preview mt-1 rounded-lg border border-zinc-200 bg-zinc-100 p-4 overflow-auto max-h-[42vh] flex flex-col items-center gap-3" data-testid="label-preview">
              {expanded.length === 0 ? (
                <p className="text-sm text-zinc-400 py-6">—</p>
              ) : expanded.map((l, i) => (activeTpl
                // 作成済みテンプレート経路は自動レイアウト(planQrLabel/planCode128Label)を
                // 通らない。つまりこの経路には 2.8/3.4mm の下限も、日本語の最小サイズも、
                // 入らない行の「落として明示」も、静粛領域の割り付けも、識別子1行の保証も
                // 無い。さらに上のバーコード形式の選択もテンプレート側の要素定義が優先する
                // ため効かない。設計者が意図して配置する前提の受容リスク（意図せず
                // 発見されるのを避けるため明記）。
                ? <LabelCanvas key={`${l.value || l.title || ''}-${i}`} template={activeTpl} data={l} w={sz.w} h={sz.h} />
                : <LabelTemplate key={`${l.value || l.title || ''}-${i}`} data={l} w={sz.w} h={sz.h} format={format} />))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="label-cancel-btn">{t('mfg.cancel')}</Button>
          {/* ロールと枚数を確認ボタン自体に出す — 一括だと誤ロールの損失が枚数分になる。 */}
          <Button onClick={doPrint} data-testid="label-print-confirm" disabled={cantPrint} className="bg-orange-500 hover:bg-orange-600">
            <Printer size={15} className="mr-1.5" />
            {t('mfg.label.printButton')}（{sz.w}{sz.h ? `×${sz.h}` : ''}mm · {expanded.length}枚）
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
